/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import axios from "axios";
import { APIGateways } from "../../../utility/APIGateways";
import buildGetURL from "../../../utility/urlHelper";
import { getAuthHeader } from "../../../utility/auth";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";

const COMPONENT_NAME = "Admin promptsManager/services/promptServices";

/**
 * Get prompts with language and namespace.
 * If namespace is not provided,
 * get back prompts for all namespaces with the given language.
 *
 * @param language - look for prompts with language
 * @param namespace - look for prompts with namespace
 */
export const getPrompts = async (language, namespace) => {
	try {
		const url = buildGetURL(APIGateways.promptsInfoEndpoint, {
			action: "prompts",
			language,
			namespace,
		});

		const result = await fetch(url, { headers: await getAuthHeader() });
		const resultPrompts = await result.json();
		const { prompts } = resultPrompts;
		const response = prompts.map((item) => {
			let ns = "";
			let promptId = "";
			const index = item.promptId.indexOf("_");
			if (index === -1) {
				// no namespace
				ns = "";
				promptId = item.promptId;
			} else {
				ns = item.promptId.substring(0, index);
				promptId = item.promptId.substring(index + 1);
			}

			const promptItem = {
				language: item.language,
				namespace: ns,
				promptId,
				prompt: item.prompt.value,
				type: item.prompt.type,
			};

			return promptItem;
		});

		return response;
	} catch (error) {
		v2.error("Error while getting prompts", {
			additionalData: { error: errorToObject(error), data: { language, namespace } },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.GET_PROMPTS_API_ERROR,
		});
		return [];
	}
};

export const addPrompt = async (language, promptId, prompt, type) => {
	try {
		const result = await fetch(APIGateways.updatePromptEndpoint, {
			method: "POST",
			headers: await getAuthHeader(),
			body: JSON.stringify({
				action: "add",
				language,
				promptId,
				prompt,
				type,
			}),
		});

		return await result.json();
	} catch (error) {
		v2.error("Error while adding prompt", {
			additionalData: { error: errorToObject(error), data: { language, promptId, prompt, type } },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.ADD_PROMPT_API_ERROR,
		});
		return error;
	}
};

export const updatePrompt = async (language, promptId, prompt, type) => {
	try {
		const result = await fetch(APIGateways.updatePromptEndpoint, {
			method: "POST",
			headers: await getAuthHeader(),
			body: JSON.stringify({
				action: "update",
				language,
				promptId,
				prompt,
				type,
			}),
		});

		return await result.json();
	} catch (error) {
		v2.error("Error while updating prompt", {
			additionalData: { error: errorToObject(error), data: { language, promptId, prompt, type } },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.UPDATE_PROMPT_API_ERROR,
		});
		return error;
	}
};

export const deletePrompt = async (language, promptId) => {
	try {
		const result = await fetch(APIGateways.updatePromptEndpoint, {
			method: "POST",
			headers: await getAuthHeader(),
			body: JSON.stringify({
				action: "delete",
				language,
				promptId,
			}),
		});

		return await result.json();
	} catch (error) {
		v2.error("Error while deleting prompt", {
			additionalData: { error: errorToObject(error), data: { language, promptId } },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.DELETE_PROMPT_API_ERROR,
		});
		return error;
	}
};

export const deleteAllPrompts = async () => {
	try {
		const result = await fetch(APIGateways.updatePromptEndpoint, {
			method: "POST",
			headers: await getAuthHeader(),
			body: JSON.stringify({
				action: "deleteAll",
			}),
		});

		return await result.json();
	} catch (error) {
		v2.error("Error while deleting all prompts", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.DELETE_PROMPTS_API_ERROR,
		});
		return error;
	}
};

/**
 * Gets the Prompts for the amazon connect instance.
 */
export const getConnectPrompts = async () => {
	try {
		let connectPrompts = await axios.get(`${APIGateways.connectLookupsEndpoint}/prompts`, {
			headers: await getAuthHeader(),
		});
		return connectPrompts.data.PromptSummaryList;
	} catch (error) {
		v2.error(`Error getting Connect prompts`, {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.GET_CONNECTPROMPTS_API_ERROR,
		});
		throw error;
	}
};
